import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Fab,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Menu,
  Slider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Map as MapIcon,
  LocationOn as LocationIcon,
  Category as CategoryIcon,
  PhotoCamera as PhotoIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  DragIndicator as DragIcon,
  TextFields as TextIcon,
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  RotateLeft as RotateIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';

function FactoryMapsApp() {
  const { token } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const svgRef = useRef(null);
  
  // State
  const [maps, setMaps] = useState([]);
  const [selectedMap, setSelectedMap] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isLocationDialogOpen, setIsLocationDialogOpen] = useState(false);
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);
  const [isLocationDetailsOpen, setIsLocationDetailsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Form states
  const [newMap, setNewMap] = useState({ title: '', factory: '', description: '' });
  const [editingLocation, setEditingLocation] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [viewingLocation, setViewingLocation] = useState(null);
  
  // Canvas states
  const [canvasSize, setCanvasSize] = useState({ width: 1200, height: 800 });
  const [selectedTool, setSelectedTool] = useState('select');
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentLine, setCurrentLine] = useState([]);
  const [draggedElement, setDraggedElement] = useState(null);
  const [resizeElement, setResizeElement] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  
  // Add new state for line management
  const [lineStart, setLineStart] = useState(null);
  const [isDrawingLine, setIsDrawingLine] = useState(false);
  const [actionHistory, setActionHistory] = useState([]);
  const [selectedLine, setSelectedLine] = useState(null);
  
  // Add drag state
  const [isDragging, setIsDragging] = useState(false);
  const [draggedLocation, setDraggedLocation] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // Add line drag state
  const [isDraggingLine, setIsDraggingLine] = useState(false);
  const [draggedLine, setDraggedLine] = useState(null);
  const [lineDragOffset, setLineDragOffset] = useState({ x: 0, y: 0 });
  
  // Add grid and resize state
  const [showGrid, setShowGrid] = useState(true);
  const [gridSize, setGridSize] = useState(10);
  const [contextMenu, setContextMenu] = useState({ open: false, x: 0, y: 0, location: null });
  const [lineContextMenu, setLineContextMenu] = useState({ open: false, x: 0, y: 0, lineIndex: null });
  const [isResizeDialogOpen, setIsResizeDialogOpen] = useState(false);
  const [resizingLocation, setResizingLocation] = useState(null);
  
  // Add new state for selected location and arrow-based resizing
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [resizeDirection, setResizeDirection] = useState(null); // 'horizontal' or 'vertical'

  // Add state for text elements
  const [editingText, setEditingText] = useState(null);
  const [isTextDialogOpen, setIsTextDialogOpen] = useState(false);
  const [selectedText, setSelectedText] = useState(null);
  const [isRotating, setIsRotating] = useState(false);
  const [rotationStartAngle, setRotationStartAngle] = useState(0);
  const [draggedText, setDraggedText] = useState(null);
  const [textDragOffset, setTextDragOffset] = useState({ x: 0, y: 0 });
  
  // Add multi-selection state
  const [selectedElements, setSelectedElements] = useState({
    locations: [],
    texts: [],
    lines: []
  });
  const [isMultiSelecting, setIsMultiSelecting] = useState(false);
  const [multiSelectOffset, setMultiSelectOffset] = useState({ x: 0, y: 0 });

  // Fetch maps
  const fetchMaps = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${config.apiUrl}/factorymaps`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch maps');
      }
      
      const data = await response.json();
      console.log('Fetched maps data:', data);
      
      // Ensure all locations have textPosition field (temporary fix for backend issue)
      const mapsWithTextPosition = data.map(map => ({
        ...map,
        locations: map.locations.map(location => ({
          ...location,
          textPosition: location.textPosition || 'bottom'
        }))
      }));
      
      // Log the first location's textPosition if it exists
      if (mapsWithTextPosition.length > 0 && mapsWithTextPosition[0].locations && mapsWithTextPosition[0].locations.length > 0) {
        console.log('First location textPosition:', mapsWithTextPosition[0].locations[0].textPosition);
      }
      setMaps(mapsWithTextPosition);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchMaps();
  }, [fetchMaps]);
  
  // Add keyboard event listener for Ctrl+A (select all)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault();
        if (selectedMap) {
          setSelectedElements({
            locations: selectedMap.locations?.map(loc => loc._id) || [],
            texts: selectedMap.texts?.map(text => text._id) || [],
            lines: selectedMap.lines?.map(line => line._id) || []
          });
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedMap]);

  // Handle text operations
  const handleAddText = async (textData) => {
    try {
      // Use the position from editingText which was set when clicking the canvas
      const position = textData.position;
      
      const newText = {
        ...textData,
        position,
        style: {
          fontSize: textData?.style?.fontSize || 16,
          fontWeight: textData?.style?.fontWeight || 'normal',
          fontStyle: textData?.style?.fontStyle || 'normal',
          color: textData?.style?.color || '#000000'
        }
      };
      
      const response = await fetch(`${config.apiUrl}/factorymaps/${selectedMap._id}/texts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newText)
      });
      
      if (!response.ok) {
        throw new Error('Failed to add text');
      }
      
      const addedText = await response.json();
      const updatedMap = { ...selectedMap, texts: [...(selectedMap.texts || []), addedText] };
      setSelectedMap(updatedMap);
      setMaps(maps.map(map => map._id === updatedMap._id ? updatedMap : map));
      setIsTextDialogOpen(false);
      setEditingText(null);
      setSelectedTool('select');
    } catch (error) {
      setError(error.message);
    }
  };

  const handleUpdateText = async (textId, textData) => {
    try {
      console.log('handleUpdateText called with:', { textId, textData });
      console.log('Current editingText:', editingText);
      console.log('Comparison:', editingText?._id, '===', textId, '=', editingText?._id === textId);
      
      const response = await fetch(`${config.apiUrl}/factorymaps/${selectedMap._id}/texts/${textId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(textData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to update text');
      }
      
      const updatedText = await response.json();
      const updatedMap = {
        ...selectedMap,
        texts: selectedMap.texts.map(text => 
          text._id === textId ? updatedText : text
        )
      };
      setSelectedMap(updatedMap);
      setMaps(maps.map(map => map._id === updatedMap._id ? updatedMap : map));
      
      // Update the selected text to the updated version
      setSelectedText(updatedText);
      
      // After updating text, close the dialog but keep the text selected
      console.log('Text updated successfully, closing dialog');
      setIsTextDialogOpen(false);
      setEditingText(null);
      // Keep the text selected so user can continue editing if needed
    } catch (error) {
      setError(error.message);
    }
  };

  const handleDeleteText = async (textId) => {
    if (!window.confirm('Are you sure you want to delete this text?')) return;
    
    try {
      const response = await fetch(`${config.apiUrl}/factorymaps/${selectedMap._id}/texts/${textId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete text');
      }
      
      const updatedMap = {
        ...selectedMap,
        texts: selectedMap.texts.filter(text => text._id !== textId)
      };
      setSelectedMap(updatedMap);
      setMaps(maps.map(map => map._id === updatedMap._id ? updatedMap : map));
      setSelectedText(null);
    } catch (error) {
      setError(error.message);
    }
  };

  // Handle SVG click for adding locations and text
  const handleSvgClick = (e) => {
    // Don't handle canvas-level clicks if we're clicking on text
    if (e.target.tagName === 'text' || e.target.closest('text')) {
      return;
    }
    
    if (selectedTool === 'text') {
      const rect = svgRef.current.getBoundingClientRect();
      const scale = rect.width / canvasSize.width;
      const x = snapToGrid((e.clientX - rect.left) / scale);
      const y = snapToGrid((e.clientY - rect.top) / scale);
      
      setEditingText({
        content: '',
        position: { x, y },
        style: {
          fontSize: 16,
          fontWeight: 'normal',
          fontStyle: 'normal',
          color: '#000000'
        }
      });
      setIsTextDialogOpen(true);
    } else if (selectedTool === 'location' && !isDrawing) {
      const rect = svgRef.current.getBoundingClientRect();
      const x = snapToGrid(e.clientX - rect.left);
      const y = snapToGrid(e.clientY - rect.top);
      
      setEditingLocation({
        name: '',
        type: 'other',
        position: { x, y },
        shape: { type: 'rectangle', width: 50, height: 30 },
        color: '#ff5722',
        description: '',
        tags: [],
        textPosition: 'bottom'
      });
      setIsLocationDialogOpen(true);
    } else if (selectedTool === 'select') {
      // Clear selected location when clicking on empty space
      setSelectedLocationId(null);
      setResizeDirection(null);
      // Don't clear selected text when clicking on empty space - let text handle its own selection
    }
  };

  // Calculate angle between two points
  const calculateAngle = (center, point) => {
    return Math.atan2(point.y - center.y, point.x - center.x) * (180 / Math.PI);
  };

  // Handle text rotation
  const handleRotationStart = (e, text) => {
    e.stopPropagation();
    setIsRotating(true);
    const rect = svgRef.current.getBoundingClientRect();
    const scale = rect.width / canvasSize.width;
    const mouseX = (e.clientX - rect.left) / scale;
    const mouseY = (e.clientY - rect.top) / scale;
    setRotationStartAngle(calculateAngle(text.position, { x: mouseX, y: mouseY }));
  };

  const handleRotation = (e, text) => {
    if (!isRotating) return;

    const rect = svgRef.current.getBoundingClientRect();
    const scale = rect.width / canvasSize.width;
    const mouseX = (e.clientX - rect.left) / scale;
    const mouseY = (e.clientY - rect.top) / scale;
    
    const currentAngle = calculateAngle(text.position, { x: mouseX, y: mouseY });
    const deltaAngle = currentAngle - rotationStartAngle;
    
    // Update text rotation
    const updatedMap = {
      ...selectedMap,
      texts: selectedMap.texts.map(t => 
        t._id === text._id 
          ? { ...t, rotation: ((t.rotation || 0) + deltaAngle) % 360 }
          : t
      )
    };
    setSelectedMap(updatedMap);
    setRotationStartAngle(currentAngle);
  };

  const handleRotationEnd = async (text) => {
    if (!isRotating) return;
    setIsRotating(false);

    // Save the final rotation to the database
    try {
      const response = await fetch(`${config.apiUrl}/factorymaps/${selectedMap._id}/texts/${text._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(text)
      });
      
      if (!response.ok) {
        throw new Error('Failed to update text rotation');
      }
    } catch (error) {
      setError(error.message);
    }
  };

  // Handle mouse events for line drawing and dragging
  const handleMouseDown = (e) => {
    // Don't handle canvas-level mouse events if we're already dragging a line
    if (isDraggingLine) {
      return;
    }
    
    // Don't handle canvas-level mouse events if we're interacting with text
    if (e.target.tagName === 'text' || e.target.closest('text')) {
      return;
    }
    
    const rect = svgRef.current.getBoundingClientRect();
    const x = snapToGrid(e.clientX - rect.left);
    const y = snapToGrid(e.clientY - rect.top);
    
    if (selectedTool === 'line') {
      if (!isDrawingLine) {
        // Start drawing line
        setIsDrawingLine(true);
        setLineStart({ x, y });
        setCurrentLine([{ x, y }]);
      } else {
        // Finish drawing line
        setIsDrawingLine(false);
        const lineData = {
          name: '',
          points: [lineStart, { x, y }],
          color: '#000000',
          strokeWidth: 2
        };
        
        // Add line to map
        handleAddLine(lineData);
        setLineStart(null);
        setCurrentLine([]);
      }
    } else if (selectedTool === 'select') {
             // Check if clicking on a line first
       const clickedLine = selectedMap.lines?.find((line, index) => {
         if (!line.points || line.points.length < 2) return false;
         
         // Calculate distance from point to line segment
         const p1 = line.points[0];
         const p2 = line.points[1];
         
         // Vector from p1 to p2
         const dx = p2.x - p1.x;
         const dy = p2.y - p1.y;
         
         // Vector from p1 to click point
         const px = x - p1.x;
         const py = y - p1.y;
         
         // Projection of click point onto line
         const t = Math.max(0, Math.min(1, (px * dx + py * dy) / (dx * dx + dy * dy)));
         
         // Closest point on line to click point
         const closestX = p1.x + t * dx;
         const closestY = p1.y + t * dy;
         
         // Distance from click point to closest point on line
         const distance = Math.sqrt(Math.pow(x - closestX, 2) + Math.pow(y - closestY, 2));
         
         // Consider line clicked if within 10 pixels
         return distance <= 10;
       });
      
      if (clickedLine) {
        // Line was clicked, let the line's own event handler deal with it
        return;
      }
      
      // Check if clicking on a location
      const clickedLocation = selectedMap.locations.find(loc => {
        if (loc.shape.type === 'circle') {
          const distance = Math.sqrt(
            Math.pow(x - loc.position.x, 2) + Math.pow(y - loc.position.y, 2)
          );
          return distance <= (loc.shape.radius || 30);
        } else {
          return x >= loc.position.x && 
                 x <= loc.position.x + loc.shape.width &&
                 y >= loc.position.y && 
                 y <= loc.position.y + loc.shape.height;
        }
      });
      
      if (clickedLocation) {
        // Handle multi-selection with Ctrl key
        if (e.ctrlKey || e.metaKey) {
          if (isElementSelected('locations', clickedLocation._id)) {
            removeFromSelection('locations', clickedLocation._id);
          } else {
            addToSelection('locations', clickedLocation._id);
          }
        } else {
          // Single selection - clear other selections and select this location
          clearSelection();
          addToSelection('locations', clickedLocation._id);
        }
        
        // Start dragging (either single or multi)
        setSelectedLocationId(clickedLocation._id);
        setSelectedLocation(clickedLocation);
        setIsDragging(true);
        setDraggedLocation(clickedLocation);
        setDragOffset({
          x: x - clickedLocation.position.x,
          y: y - clickedLocation.position.y
        });
        
                 // If we have multiple elements selected, start multi-selection dragging
         if (getSelectedCount() > 1) {
           console.log(`Starting multi-selection drag with ${getSelectedCount()} elements selected`);
           console.log('Selected elements:', selectedElements);
           setIsMultiSelecting(true);
           setMultiSelectOffset({
             x: x - clickedLocation.position.x,
             y: y - clickedLocation.position.y
           });
         }
      } else {
        // Clicked on empty space - clear selection unless Ctrl is held
        if (!e.ctrlKey && !e.metaKey) {
          clearSelection();
          setSelectedLocationId(null);
          setResizeDirection(null);
        }
      }
    }
  };

  const handleMouseMove = (e) => {
    if (!svgRef.current || !selectedMap) return;
    
    console.log('handleMouseMove called:', { 
      isDraggingLine, 
      draggedLine, 
      isDragging, 
      draggedLocation, 
      draggedText, 
      getSelectedCount: getSelectedCount(),
      isMultiSelecting 
    });
    
    const rect = svgRef.current.getBoundingClientRect();
    const scale = rect.width / canvasSize.width;
    const x = snapToGrid((e.clientX - rect.left) / scale);
    const y = snapToGrid((e.clientY - rect.top) / scale);
    setMousePos({ x, y });

    if (isDrawingLine && selectedTool === 'line') {
      // Update current line for preview with grid snapping
      setCurrentLine([lineStart, { x, y }]);
    } else if (isDraggingLine && draggedLine) {
      // Update dragged line position
      const newX = snapToGrid(x - lineDragOffset.x);
      const newY = snapToGrid(y - lineDragOffset.y);
      
      // Calculate the movement delta
      const deltaX = newX - (draggedLine.points[0].x + draggedLine.points[1].x) / 2;
      const deltaY = newY - (draggedLine.points[0].y + draggedLine.points[1].y) / 2;
      
      if (getSelectedCount() > 1) {
        // Move all selected elements together
        console.log('Multi-selection line movement detected!');
        console.log('Selected count:', getSelectedCount());
        console.log('Selected elements:', selectedElements);
        console.log('Dragged line:', draggedLine);
        console.log('New position:', { x: newX, y: newY });
        console.log('Original position:', { x: (draggedLine.points[0].x + draggedLine.points[1].x) / 2, y: (draggedLine.points[0].y + draggedLine.points[1].y) / 2 });
        
        // Calculate the delta from the original position BEFORE updating
        const originalCenterX = (draggedLine.points[0].x + draggedLine.points[1].x) / 2;
        const originalCenterY = (draggedLine.points[0].y + draggedLine.points[1].y) / 2;
        const actualDeltaX = newX - originalCenterX;
        const actualDeltaY = newY - originalCenterY;
        
        console.log(`Calculated delta: (${actualDeltaX}, ${actualDeltaY}) from original position (${originalCenterX}, ${originalCenterY}) to new position (${newX}, ${newY})`);
        
        // First, update the dragged line to its new position
        const updatedDraggedLine = {
          ...draggedLine,
          points: [
            {
              x: draggedLine.points[0].x + actualDeltaX,
              y: draggedLine.points[0].y + actualDeltaY
            },
            {
              x: draggedLine.points[1].x + actualDeltaX,
              y: draggedLine.points[1].y + actualDeltaY
            }
          ]
        };
        
        const updatedMap = {
          ...selectedMap,
          locations: selectedMap.locations?.map(loc => {
            if (isElementSelected('locations', loc._id)) {
              const newPos = { x: loc.position.x + actualDeltaX, y: loc.position.y + actualDeltaY };
              console.log(`Moving location ${loc.name} from (${loc.position.x}, ${loc.position.y}) to (${newPos.x}, ${newPos.y}) with delta (${actualDeltaX}, ${actualDeltaY})`);
              return { ...loc, position: newPos };
            }
            return loc;
          }) || [],
          texts: selectedMap.texts?.map(text => {
            if (isElementSelected('texts', text._id)) {
              const newPos = { x: text.position.x + actualDeltaX, y: text.position.y + actualDeltaY };
              console.log(`Moving text "${text.content}" from (${text.position.x}, ${text.position.y}) to (${newPos.x}, ${newPos.y}) with delta (${actualDeltaX}, ${actualDeltaY})`);
              return { ...text, position: newPos };
            }
            return text;
          }) || [],
          lines: selectedMap.lines?.map(line => {
            if (line._id === draggedLine._id) {
              // Update the dragged line to its new position
              return updatedDraggedLine;
            } else if (isElementSelected('lines', line._id)) {
              const newPoints = line.points.map(point => ({
                x: point.x + actualDeltaX,
                y: point.y + actualDeltaY
              }));
              console.log(`Moving line from (${line.points[0].x}, ${line.points[0].y}) to (${newPoints[0].x}, ${newPoints[0].y}) with delta (${actualDeltaX}, ${actualDeltaY})`);
              return {
                ...line,
                points: newPoints
              };
            }
            return line;
          }) || []
        };
        setSelectedMap(updatedMap);
      } else {
        // Single line movement
        const updatedLine = {
          ...draggedLine,
          points: [
            {
              x: draggedLine.points[0].x + deltaX,
              y: draggedLine.points[0].y + deltaY
            },
            {
              x: draggedLine.points[1].x + deltaX,
              y: draggedLine.points[1].y + deltaY
            }
          ]
        };
        
        const updatedMap = {
          ...selectedMap,
          lines: selectedMap.lines.map(line => 
            line._id === draggedLine._id ? updatedLine : line
          )
        };
        setSelectedMap(updatedMap);
      }
         } else if (isDragging && draggedLocation) {
       // Update dragged location position
       const newX = snapToGrid(x - dragOffset.x);
       const newY = snapToGrid(y - dragOffset.y);
       
                       // Always check if we have multiple elements selected and move them together
        if (getSelectedCount() > 1) {
          // Move all selected elements together
          console.log('Multi-selection movement detected!');
          console.log('Selected count:', getSelectedCount());
          console.log('Selected elements:', selectedElements);
          console.log('Dragged location:', draggedLocation);
          console.log('New position:', { x: newX, y: newY });
          console.log('Original position:', { x: draggedLocation.position.x, y: draggedLocation.position.y });
          
          // Calculate the delta from the original position BEFORE updating
          const originalX = draggedLocation.position.x;
          const originalY = draggedLocation.position.y;
          const deltaX = newX - originalX;
          const deltaY = newY - originalY;
          
          console.log(`Calculated delta: (${deltaX}, ${deltaY}) from original position (${originalX}, ${originalY}) to new position (${newX}, ${newY})`);
          
          // First, update the dragged location to its new position
          const updatedDraggedLocation = { ...draggedLocation, position: { x: newX, y: newY } };
         
          const updatedMap = {
            ...selectedMap,
            locations: selectedMap.locations.map(loc => {
              if (loc._id === draggedLocation._id) {
                // Update the dragged location to its new position
                return updatedDraggedLocation;
              } else if (isElementSelected('locations', loc._id)) {
                // Move other selected locations by the same delta
                const newPos = { x: loc.position.x + deltaX, y: loc.position.y + deltaY };
                console.log(`Moving location ${loc.name} from (${loc.position.x}, ${loc.position.y}) to (${newPos.x}, ${newPos.y}) with delta (${deltaX}, ${deltaY})`);
                return { ...loc, position: newPos };
              }
              return loc;
            }),
            texts: selectedMap.texts?.map(text => {
              if (isElementSelected('texts', text._id)) {
                const newPos = { x: text.position.x + deltaX, y: text.position.y + deltaY };
                console.log(`Moving text "${text.content}" from (${text.position.x}, ${text.position.y}) to (${newPos.x}, ${newPos.y}) with delta (${deltaX}, ${deltaY})`);
                return { ...text, position: newPos };
              }
              return text;
            }) || [],
            lines: selectedMap.lines?.map(line => {
              if (isElementSelected('lines', line._id)) {
                const newPoints = line.points.map(point => ({
                  x: point.x + deltaX,
                  y: point.y + deltaY
                }));
                console.log(`Moving line from (${line.points[0].x}, ${line.points[0].y}) to (${newPoints[0].x}, ${newPoints[0].y}) with delta (${deltaX}, ${deltaY})`);
                return {
                  ...line,
                  points: newPoints
                };
              }
              return line;
            }) || []
          };
          setSelectedMap(updatedMap);
        } else {
         // Single location movement
         const updatedMap = {
           ...selectedMap,
           locations: selectedMap.locations.map(loc => 
             loc._id === draggedLocation._id 
               ? { ...loc, position: { x: newX, y: newY } }
               : loc
           )
         };
         setSelectedMap(updatedMap);
       }
         } else if (draggedText) {
       // Update dragged text position
       const newX = snapToGrid(x - textDragOffset.x);
       const newY = snapToGrid(y - textDragOffset.y);
       
       if (getSelectedCount() > 1) {
         // Move all selected elements together
         console.log('Multi-selection text movement detected!');
         console.log('Selected count:', getSelectedCount());
         console.log('Selected elements:', selectedElements);
         console.log('Dragged text:', draggedText);
         console.log('New position:', { x: newX, y: newY });
         console.log('Original position:', { x: draggedText.position.x, y: draggedText.position.y });
         
         // Calculate the delta from the original position BEFORE updating
         const originalX = draggedText.position.x;
         const originalY = draggedText.position.y;
         const actualDeltaX = newX - originalX;
         const actualDeltaY = newY - originalY;
         
         console.log(`Calculated delta: (${actualDeltaX}, ${actualDeltaY}) from original position (${originalX}, ${originalY}) to new position (${newX}, ${newY})`);
         
         // First, update the dragged text to its new position
         const updatedDraggedText = { ...draggedText, position: { x: newX, y: newY } };
         
         const updatedMap = {
           ...selectedMap,
           locations: selectedMap.locations?.map(loc => {
             if (isElementSelected('locations', loc._id)) {
               const newPos = { x: loc.position.x + actualDeltaX, y: loc.position.y + actualDeltaY };
               console.log(`Moving location ${loc.name} from (${loc.position.x}, ${loc.position.y}) to (${newPos.x}, ${newPos.y}) with delta (${actualDeltaX}, ${actualDeltaY})`);
               return { ...loc, position: newPos };
             }
             return loc;
           }) || [],
           texts: selectedMap.texts?.map(text => {
             if (text._id === draggedText._id) {
               // Update the dragged text to its new position
               return updatedDraggedText;
             } else if (isElementSelected('texts', text._id)) {
               const newPos = { x: text.position.x + actualDeltaX, y: text.position.y + actualDeltaY };
               console.log(`Moving text "${text.content}" from (${text.position.x}, ${text.position.y}) to (${newPos.x}, ${newPos.y}) with delta (${actualDeltaX}, ${actualDeltaY})`);
               return { ...text, position: newPos };
             }
             return text;
           }) || [],
           lines: selectedMap.lines?.map(line => {
             if (isElementSelected('lines', line._id)) {
               const newPoints = line.points.map(point => ({
                 x: point.x + actualDeltaX,
                 y: point.y + actualDeltaY
               }));
               console.log(`Moving line from (${line.points[0].x}, ${line.points[0].y}) to (${newPoints[0].x}, ${newPoints[0].y}) with delta (${actualDeltaX}, ${actualDeltaY})`);
               return {
                 ...line,
                 points: newPoints
               };
             }
             return line;
           }) || []
         };
         setSelectedMap(updatedMap);
       } else {
         // Single text movement
         const updatedMap = {
           ...selectedMap,
           texts: selectedMap.texts.map(text => 
             text._id === draggedText._id 
               ? { ...text, position: { x: newX, y: newY } }
               : text
           )
         };
         setSelectedMap(updatedMap);
       }
    } else if (resizeElement && resizeDirection) {
      // Handle arrow-based resizing
      handleArrowResize(e, resizeElement);
    }
  };

  const handleMouseUp = () => {
    if (isDragging && draggedLocation) {
      // Finish dragging location
      setIsDragging(false);
      setDraggedLocation(null);
      setDragOffset({ x: 0, y: 0 });
      
      // Update maps array
      setMaps(maps.map(map => map._id === selectedMap._id ? selectedMap : map));
      
             if (getSelectedCount() > 1) {
         // Save all selected elements to database
         console.log('Finishing multi-selection drag, saving all elements');
         const updatedMap = selectedMap;
         
         // Save updated locations
         selectedElements.locations.forEach(locationId => {
           const updatedLocation = updatedMap.locations.find(loc => loc._id === locationId);
           if (updatedLocation) {
             console.log(`Saving location ${updatedLocation.name} at position (${updatedLocation.position.x}, ${updatedLocation.position.y})`);
             handleUpdateLocationPosition(locationId, updatedLocation.position);
           }
         });
         
         // Save updated texts
         selectedElements.texts.forEach(textId => {
           const updatedText = updatedMap.texts.find(text => text._id === textId);
           if (updatedText) {
             console.log(`Saving text "${updatedText.content}" at position (${updatedText.position.x}, ${updatedText.position.y})`);
             handleUpdateText(textId, updatedText);
           }
         });
         
         // Save updated lines
         selectedElements.lines.forEach(lineId => {
           const updatedLine = updatedMap.lines.find(line => line._id === lineId);
           if (updatedLine) {
             console.log(`Saving line at position (${updatedLine.points[0].x}, ${updatedLine.points[0].y})`);
             handleUpdateLine(updatedLine);
           }
         });
         
         // Reset multi-selection state
         setIsMultiSelecting(false);
         setMultiSelectOffset({ x: 0, y: 0 });
       } else {
        // Single location movement
        const updatedLocation = selectedMap.locations.find(loc => loc._id === draggedLocation._id);
        if (updatedLocation) {
          // Save the new position to the database
          handleUpdateLocationPosition(draggedLocation._id, updatedLocation.position);
        }
      }
    }
    
         if (isDraggingLine && draggedLine) {
       // Finish dragging line
       setIsDraggingLine(false);
       setDraggedLine(null);
       setLineDragOffset({ x: 0, y: 0 });
       
       // Update maps array
       setMaps(maps.map(map => map._id === selectedMap._id ? selectedMap : map));
       
       if (getSelectedCount() > 1) {
         // Save all selected elements to database
         console.log('Finishing multi-selection line drag, saving all elements');
         const updatedMap = selectedMap;
         
         // Save updated locations
         selectedElements.locations.forEach(locationId => {
           const updatedLocation = updatedMap.locations.find(loc => loc._id === locationId);
           if (updatedLocation) {
             console.log(`Saving location ${updatedLocation.name} at position (${updatedLocation.position.x}, ${updatedLocation.position.y})`);
             handleUpdateLocationPosition(locationId, updatedLocation.position);
           }
         });
         
         // Save updated texts
         selectedElements.texts.forEach(textId => {
           const updatedText = updatedMap.texts.find(text => text._id === textId);
           if (updatedText) {
             console.log(`Saving text "${updatedText.content}" at position (${updatedText.position.x}, ${updatedText.position.y})`);
             handleUpdateText(textId, updatedText);
           }
         });
         
         // Save updated lines
         selectedElements.lines.forEach(lineId => {
           const updatedLine = updatedMap.lines.find(line => line._id === lineId);
           if (updatedLine) {
             console.log(`Saving line at position (${updatedLine.points[0].x}, ${updatedLine.points[0].y})`);
             handleUpdateLine(updatedLine);
           }
         });
         
         // Reset multi-selection state
         setIsMultiSelecting(false);
         setMultiSelectOffset({ x: 0, y: 0 });
       } else {
         // Single line movement
         const updatedLine = selectedMap.lines.find(line => line._id === draggedLine._id);
         if (updatedLine) {
           handleUpdateLine(updatedLine);
         }
       }
     } else if (draggedText) {
       // Finish dragging text
       const updatedText = selectedMap.texts.find(text => text._id === draggedText._id);
       if (updatedText) {
         // Save the new position to the database
         handleUpdateText(draggedText._id, updatedText);
       }
       
       if (getSelectedCount() > 1) {
         // Save all selected elements to database
         console.log('Finishing multi-selection text drag, saving all elements');
         const updatedMap = selectedMap;
         
         // Save updated locations
         selectedElements.locations.forEach(locationId => {
           const updatedLocation = updatedMap.locations.find(loc => loc._id === locationId);
           if (updatedLocation) {
             console.log(`Saving location ${updatedLocation.name} at position (${updatedLocation.position.x}, ${updatedLocation.position.y})`);
             handleUpdateLocationPosition(locationId, updatedLocation.position);
           }
         });
         
         // Save updated texts
         selectedElements.texts.forEach(textId => {
           const updatedText = updatedMap.texts.find(text => text._id === textId);
           if (updatedText) {
             console.log(`Saving text "${updatedText.content}" at position (${updatedText.position.x}, ${updatedText.position.y})`);
             handleUpdateText(textId, updatedText);
           }
         });
         
         // Save updated lines
         selectedElements.lines.forEach(lineId => {
           const updatedLine = updatedMap.lines.find(line => line._id === lineId);
           if (updatedLine) {
             console.log(`Saving line at position (${updatedLine.points[0].x}, ${updatedLine.points[0].y})`);
             handleUpdateLine(updatedLine);
           }
         });
         
         // Reset multi-selection state
         setIsMultiSelecting(false);
         setMultiSelectOffset({ x: 0, y: 0 });
       }
       
       setDraggedText(null);
       setTextDragOffset({ x: 0, y: 0 });
    } else if (resizeElement && resizeDirection) {
      // Finish arrow-based resizing
      handleArrowResizeEnd(resizeElement);
    }
  };

  // Handle line mouse down for dragging
  const handleLineMouseDown = (e, line, index) => {
    e.stopPropagation();
    console.log('Line mouse down triggered:', { line, index, selectedTool, getSelectedCount: getSelectedCount() });
    
    if (selectedTool === 'select') {
      // Handle multi-selection with Ctrl key
      if (e.ctrlKey || e.metaKey) {
        if (isElementSelected('lines', line._id)) {
          removeFromSelection('lines', line._id);
        } else {
          addToSelection('lines', line._id);
        }
      } else {
        // Single selection - clear other selections and select this line
        clearSelection();
        addToSelection('lines', line._id);
      }
      
      console.log('After selection update:', { selectedElements, getSelectedCount: getSelectedCount() });
      
      setSelectedLine(index);
      setIsDraggingLine(true);
      setDraggedLine(line);
      
      const rect = svgRef.current.getBoundingClientRect();
      const x = snapToGrid(e.clientX - rect.left);
      const y = snapToGrid(e.clientY - rect.top);
      
      // Calculate offset from line center
      const lineCenterX = (line.points[0].x + line.points[1].x) / 2;
      const lineCenterY = (line.points[0].y + line.points[1].y) / 2;
      
      setLineDragOffset({
        x: x - lineCenterX,
        y: y - lineCenterY
      });
      
      // If we have multiple elements selected, start multi-selection dragging
      if (getSelectedCount() > 1) {
        console.log(`Starting multi-selection line drag with ${getSelectedCount()} elements selected`);
        console.log('Selected elements:', selectedElements);
        setIsMultiSelecting(true);
        setMultiSelectOffset({
          x: x - lineCenterX,
          y: y - lineCenterY
        });
      } else {
        console.log('Single line selection, not starting multi-selection');
      }
    }
  };

  // Add function to update location position in database
  const handleUpdateLocationPosition = async (locationId, newPosition) => {
    try {
      const response = await fetch(`${config.apiUrl}/factorymaps/${selectedMap._id}/locations/${locationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ position: newPosition })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update location position');
      }
      
      // Optionally refresh the map data to ensure consistency
      const updatedLocation = await response.json();
      const updatedMap = {
        ...selectedMap,
        locations: selectedMap.locations.map(loc => 
          loc._id === locationId ? updatedLocation : loc
        )
      };
      setSelectedMap(updatedMap);
      setMaps(maps.map(map => map._id === updatedMap._id ? updatedMap : map));
      
    } catch (error) {
      console.error('Failed to save location position:', error);
      setError('Failed to save location position');
    }
  };

  // Add function to update line in database
  const handleUpdateLine = async (updatedLine) => {
    try {
      const response = await fetch(`${config.apiUrl}/factorymaps/${selectedMap._id}/lines/${updatedLine._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updatedLine)
      });
      
      if (!response.ok) {
        throw new Error('Failed to update line');
      }
      
      // Optionally refresh the line data to ensure consistency
      const savedLine = await response.json();
      const updatedMap = {
        ...selectedMap,
        lines: selectedMap.lines.map(line => 
          line._id === updatedLine._id ? savedLine : line
        )
      };
      setSelectedMap(updatedMap);
      setMaps(maps.map(map => map._id === updatedMap._id ? updatedMap : map));
      
    } catch (error) {
      console.error('Failed to save line:', error);
      setError('Failed to save line');
    }
  };

  // Add undo functionality
  const handleUndo = () => {
    if (actionHistory.length > 0) {
      const lastAction = actionHistory[actionHistory.length - 1];
      
      if (lastAction.type === 'addLocation') {
        // Remove last added location
        const updatedMap = {
          ...selectedMap,
          locations: selectedMap.locations.filter(loc => loc._id !== lastAction.itemId)
        };
        setSelectedMap(updatedMap);
        setMaps(maps.map(map => map._id === updatedMap._id ? updatedMap : map));
      } else if (lastAction.type === 'addLine') {
        // Remove last added line
        const updatedMap = {
          ...selectedMap,
          lines: selectedMap.lines.filter((_, index) => index !== lastAction.itemIndex)
        };
        setSelectedMap(updatedMap);
        setMaps(maps.map(map => map._id === updatedMap._id ? updatedMap : map));
      }
      
      // Remove action from history
      setActionHistory(actionHistory.slice(0, -1));
    }
  };

  // Delete selected line
  const handleDeleteLine = (lineIndex) => {
    if (window.confirm('Are you sure you want to delete this line?')) {
      const updatedMap = {
        ...selectedMap,
        lines: selectedMap.lines.filter((_, index) => index !== lineIndex)
      };
      setSelectedMap(updatedMap);
      setMaps(maps.map(map => map._id === updatedMap._id ? updatedMap : map));
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, locationId) => {
    setDraggedElement(locationId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (draggedElement) {
      const rect = svgRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      // Update location position
      const updatedMap = {
        ...selectedMap,
        locations: selectedMap.locations.map(loc => 
          loc._id === draggedElement 
            ? { ...loc, position: { x: snapToGrid(x), y: snapToGrid(y) } }
            : loc
        )
      };
      setSelectedMap(updatedMap);
      setMaps(maps.map(map => map._id === updatedMap._id ? updatedMap : map));
      setDraggedElement(null);
    }
  };

  // Resize handlers
  // New arrow-based resize functions
  const handleArrowResizeStart = (locationId, direction) => {
    setResizeElement(locationId);
    setResizeDirection(direction);
  };

  const handleArrowResize = (e, locationId) => {
    if (resizeElement === locationId && resizeDirection) {
      const rect = svgRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      
      const location = selectedMap.locations.find(loc => loc._id === locationId);
      if (location) {
        let newShape = { ...location.shape };
        
        if (location.shape.type === 'circle') {
          // For circles, only resize in the direction of the arrow
          const distance = Math.sqrt(
            Math.pow(mouseX - location.position.x, 2) + Math.pow(mouseY - location.position.y, 2)
          );
          const minRadius = 10;
          const constrainedRadius = Math.max(minRadius, distance);
          newShape.radius = snapToGrid(constrainedRadius);
        } else {
          // For rectangles, resize based on direction
          if (resizeDirection === 'horizontal') {
            const newWidth = mouseX - location.position.x;
            const minWidth = 20;
            const constrainedWidth = Math.max(minWidth, newWidth);
            newShape.width = snapToGrid(constrainedWidth);
          } else if (resizeDirection === 'vertical') {
            const newHeight = mouseY - location.position.y;
            const minHeight = 15;
            const constrainedHeight = Math.max(minHeight, newHeight);
            newShape.height = snapToGrid(constrainedHeight);
          }
        }
        
        const updatedMap = {
          ...selectedMap,
          locations: selectedMap.locations.map(loc => 
            loc._id === locationId 
              ? { ...loc, shape: newShape }
              : loc
          )
        };
        setSelectedMap(updatedMap);
        setMaps(maps.map(map => map._id === updatedMap._id ? updatedMap : map));
      }
    }
  };

  const handleArrowResizeEnd = (locationId) => {
    if (resizeElement === locationId) {
      const updatedLocation = selectedMap.locations.find(loc => loc._id === locationId);
      if (updatedLocation) {
        handleUpdateLocationSize(locationId, updatedLocation.shape);
      }
      setResizeElement(null);
      setResizeDirection(null);
    }
  };



  // Add function to update location size in database
  const handleUpdateLocationSize = async (locationId, newShape) => {
    try {
      const response = await fetch(`${config.apiUrl}/factorymaps/${selectedMap._id}/locations/${locationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ shape: newShape })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update location size');
      }
      
      // Optionally refresh the map data to ensure consistency
      const updatedLocation = await response.json();
      const updatedMap = {
        ...selectedMap,
        locations: selectedMap.locations.map(loc => 
          loc._id === locationId ? updatedLocation : loc
        )
      };
      setSelectedMap(updatedMap);
      setMaps(maps.map(map => map._id === updatedMap._id ? updatedMap : map));
      
    } catch (error) {
      console.error('Failed to save location size:', error);
      setError('Failed to save location size');
    }
  };

  // Create new map
  const handleCreateMap = async () => {
    try {
      const response = await fetch(`${config.apiUrl}/factorymaps`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newMap,
          canvas: { width: 1200, height: 800, background: '#ffffff' }
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to create map');
      }
      
      const createdMap = await response.json();
      setMaps([createdMap, ...maps]);
      setSelectedMap(createdMap);
      setIsCreateDialogOpen(false);
      setNewMap({ title: '', factory: '', description: '' });
    } catch (error) {
      setError(error.message);
    }
  };

  // Update map
  const handleUpdateMap = async () => {
    try {
      const response = await fetch(`${config.apiUrl}/factorymaps/${selectedMap._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(selectedMap)
      });
      
      if (!response.ok) {
        throw new Error('Failed to update map');
      }
      
      const updatedMap = await response.json();
      setMaps(maps.map(map => map._id === updatedMap._id ? updatedMap : map));
      setSelectedMap(updatedMap);
      setIsEditDialogOpen(false);
    } catch (error) {
      setError(error.message);
    }
  };

  // Delete map
  const handleDeleteMap = async (mapId) => {
    if (!window.confirm('Are you sure you want to delete this map?')) return;
    
    try {
      const response = await fetch(`${config.apiUrl}/factorymaps/${mapId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete map');
      }
      
      setMaps(maps.filter(map => map._id !== mapId));
      if (selectedMap?._id === mapId) {
        setSelectedMap(null);
      }
    } catch (error) {
      setError(error.message);
    }
  };

  // Modified Add location to track history
  const handleAddLocation = async (locationData) => {
    try {
      // Ensure textPosition is always included
      const dataToSend = {
        ...locationData,
        textPosition: locationData.textPosition || 'bottom'
      };
      
      const response = await fetch(`${config.apiUrl}/factorymaps/${selectedMap._id}/locations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(dataToSend)
      });
      
      if (!response.ok) {
        throw new Error('Failed to add location');
      }
      
      const newLocation = await response.json();
      const updatedMap = { ...selectedMap, locations: [...selectedMap.locations, newLocation] };
      setSelectedMap(updatedMap);
      setMaps(maps.map(map => map._id === updatedMap._id ? updatedMap : map));
      
      // Add to action history
      setActionHistory([...actionHistory, { type: 'addLocation', itemId: newLocation._id }]);
      
      setIsLocationDialogOpen(false);
      setEditingLocation(null);
    } catch (error) {
      setError(error.message);
    }
  };

  // Modified Add line to track history
  const handleAddLine = async (lineData) => {
    try {
      const response = await fetch(`${config.apiUrl}/factorymaps/${selectedMap._id}/lines`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(lineData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to add line');
      }
      
      const newLine = await response.json();
      const updatedMap = { ...selectedMap, lines: [...(selectedMap.lines || []), newLine] };
      setSelectedMap(updatedMap);
      setMaps(maps.map(map => map._id === updatedMap._id ? updatedMap : map));
      
      // Add to action history
      setActionHistory([...actionHistory, { type: 'addLine', itemIndex: (selectedMap.lines || []).length }]);
    } catch (error) {
      setError(error.message);
    }
  };

  // Update location
  const handleUpdateLocation = async (locationId, locationData) => {
    try {
      // Ensure textPosition is always included
      const dataToSend = {
        ...locationData,
        textPosition: locationData.textPosition || 'bottom'
      };
      
      console.log('Updating location with data:', dataToSend);
      console.log('textPosition in dataToSend:', dataToSend.textPosition);
      console.log('JSON.stringify(dataToSend):', JSON.stringify(dataToSend));
      
      const response = await fetch(`${config.apiUrl}/factorymaps/${selectedMap._id}/locations/${locationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(dataToSend)
      });
      
      if (!response.ok) {
        throw new Error('Failed to update location');
      }
      
      const updatedLocation = await response.json();
      console.log('Updated location from server:', updatedLocation);
      console.log('textPosition in updatedLocation:', updatedLocation.textPosition);
      
      // Ensure textPosition field exists (temporary fix for backend issue)
      if (!updatedLocation.textPosition) {
        updatedLocation.textPosition = editingLocation.textPosition || 'bottom';
      }
      
      const updatedMap = {
        ...selectedMap,
        locations: selectedMap.locations.map(loc => 
          loc._id === locationId ? updatedLocation : loc
        )
      };
      setSelectedMap(updatedMap);
      setMaps(maps.map(map => map._id === updatedMap._id ? updatedMap : map));
      setIsLocationDialogOpen(false);
      setEditingLocation(null);
    } catch (error) {
      setError(error.message);
    }
  };

  // Delete location
  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;
    
    try {
      const response = await fetch(`${config.apiUrl}/factorymaps/${selectedMap._id}/locations/${locationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete location');
      }
      
      const updatedMap = {
        ...selectedMap,
        locations: selectedMap.locations.filter(loc => loc._id !== locationId)
      };
      setSelectedMap(updatedMap);
      setMaps(maps.map(map => map._id === updatedMap._id ? updatedMap : map));
    } catch (error) {
      setError(error.message);
    }
  };

  // Search locations
  const handleSearch = async () => {
    try {
      const response = await fetch(`${config.apiUrl}/factorymaps/search/locations?query=${searchQuery}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to search locations');
      }
      
      const results = await response.json();
      setSearchResults(results);
    } catch (error) {
      setError(error.message);
    }
  };

  // Handle photo upload
  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditingLocation(prev => ({ ...prev, photo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Grid snapping function
  const snapToGrid = (value) => {
    return Math.round(value / gridSize) * gridSize;
  };
  
  // Multi-selection helper functions
  const addToSelection = (type, id) => {
    console.log(`addToSelection called: type=${type}, id=${id}`);
    setSelectedElements(prev => {
      const newState = {
        ...prev,
        [type]: [...prev[type], id]
      };
      console.log(`addToSelection: prev=${JSON.stringify(prev)}, newState=${JSON.stringify(newState)}`);
      return newState;
    });
  };
  
  const removeFromSelection = (type, id) => {
    console.log(`removeFromSelection called: type=${type}, id=${id}`);
    setSelectedElements(prev => {
      const newState = {
        ...prev,
        [type]: prev[type].filter(itemId => itemId !== id)
      };
      console.log(`removeFromSelection: prev=${JSON.stringify(prev)}, newState=${JSON.stringify(newState)}`);
      return newState;
    });
  };
  
  const clearSelection = () => {
    console.log('clearSelection called');
    setSelectedElements({
      locations: [],
      texts: [],
      lines: []
    });
  };
  
  const isElementSelected = (type, id) => {
    const result = selectedElements[type].includes(id);
    console.log(`isElementSelected: type=${type}, id=${id}, result=${result}`);
    return result;
  };
  
  const getSelectedCount = () => {
    const count = selectedElements.locations.length + selectedElements.texts.length + selectedElements.lines.length;
    console.log(`getSelectedCount called: ${count} (L:${selectedElements.locations.length} T:${selectedElements.texts.length} L:${selectedElements.lines.length})`);
    return count;
  };

  // Calculate text position based on textPosition setting
  const getTextPosition = (position, shape, textPosition) => {
    const textOffset = 15; // Distance from shape edge
    
    switch (textPosition) {
      case 'top':
        return {
          x: position.x + (shape.type === 'circle' ? 0 : shape.width / 2),
          y: position.y - textOffset,
          textAnchor: 'middle',
          dominantBaseline: 'auto'
        };
      case 'bottom':
        return {
          x: position.x + (shape.type === 'circle' ? 0 : shape.width / 2),
          y: position.y + (shape.type === 'circle' ? shape.radius + textOffset : shape.height + textOffset),
          textAnchor: 'middle',
          dominantBaseline: 'hanging'
        };
      case 'left':
        return {
          x: position.x - textOffset,
          y: position.y + (shape.type === 'circle' ? 0 : shape.height / 2),
          textAnchor: 'end',
          dominantBaseline: 'middle'
        };
      case 'right':
        return {
          x: position.x + (shape.type === 'circle' ? shape.radius + textOffset : shape.width + textOffset),
          y: position.y + (shape.type === 'circle' ? 0 : shape.height / 2),
          textAnchor: 'start',
          dominantBaseline: 'middle'
        };
      default:
        return {
          x: position.x + (shape.type === 'circle' ? 0 : shape.width / 2),
          y: position.y + (shape.type === 'circle' ? shape.radius + textOffset : shape.height + textOffset),
          textAnchor: 'middle',
          dominantBaseline: 'hanging'
        };
    }
  };

  // Handle right-click context menu for locations
  const handleContextMenu = (e, location) => {
    e.preventDefault();
    setContextMenu({
      open: true,
      x: e.clientX,
      y: e.clientY,
      location
    });
  };

  // Handle right-click context menu for lines
  const handleLineContextMenu = (e, lineIndex) => {
    e.preventDefault();
    setLineContextMenu({
      open: true,
      x: e.clientX,
      y: e.clientY,
      lineIndex
    });
  };

  // Handle line color change
  const handleLineColorChange = async (lineIndex, newColor) => {
    if (selectedMap && lineIndex !== null) {
      const line = selectedMap.lines[lineIndex];
      if (line) {
        const updatedLine = { ...line, color: newColor };
        
        try {
          const response = await fetch(`${config.apiUrl}/factorymaps/${selectedMap._id}/lines/${line._id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ color: newColor })
          });
          
          if (!response.ok) {
            throw new Error('Failed to update line color');
          }
          
          const updatedMap = {
            ...selectedMap,
            lines: selectedMap.lines.map((l, idx) => idx === lineIndex ? updatedLine : l)
          };
          setSelectedMap(updatedMap);
          setMaps(maps.map(map => map._id === updatedMap._id ? updatedMap : map));
          
        } catch (error) {
          console.error('Failed to update line color:', error);
          setError('Failed to update line color');
        }
      }
    }
    setLineContextMenu({ open: false, x: 0, y: 0, lineIndex: null });
  };

  // Handle manual resize dialog
  const handleManualResize = (location) => {
    setResizingLocation(location);
    setIsResizeDialogOpen(true);
    setContextMenu({ open: false, x: 0, y: 0, location: null });
  };

  // Apply manual resize
  const applyManualResize = () => {
    if (resizingLocation) {
      const updatedLocation = {
        ...resizingLocation,
        shape: {
          ...resizingLocation.shape,
          width: parseInt(resizingLocation.shape.width),
          height: parseInt(resizingLocation.shape.height),
          radius: resizingLocation.shape.type === 'circle' ? parseInt(resizingLocation.shape.radius) : undefined
        }
      };
      
      const updatedMap = {
        ...selectedMap,
        locations: selectedMap.locations.map(loc => 
          loc._id === resizingLocation._id ? updatedLocation : loc
        )
      };
      setSelectedMap(updatedMap);
      setMaps(maps.map(map => map._id === updatedMap._id ? updatedMap : map));
      
      // Save to database
      handleUpdateLocationSize(resizingLocation._id, updatedLocation.shape);
      
      setIsResizeDialogOpen(false);
      setResizingLocation(null);
    }
  };

  // Render grid
  const renderGrid = () => {
    if (!showGrid) return null;
    
    const lines = [];
    const { width, height } = canvasSize;
    
    // Vertical lines
    for (let x = 0; x <= width; x += gridSize) {
      lines.push(
        <line
          key={`v${x}`}
          x1={x}
          y1={0}
          x2={x}
          y2={height}
          stroke="#e0e0e0"
          strokeWidth="1"
          opacity="0.5"
        />
      );
    }
    
    // Horizontal lines
    for (let y = 0; y <= height; y += gridSize) {
      lines.push(
        <line
          key={`h${y}`}
          x1={0}
          y1={y}
          x2={width}
          y2={y}
          stroke="#e0e0e0"
          strokeWidth="1"
          opacity="0.5"
        />
      );
    }
    
    return lines;
  };

  // Render location as SVG element
  const renderLocation = (location) => {
    const { position, shape, color, name } = location;
    const textPosition = location.textPosition || 'bottom';
    const isSelected = selectedLocationId === location._id || isElementSelected('locations', location._id);
    
    if (shape.type === 'circle') {
      return (
        <g key={location._id}>
          <circle
            cx={position.x}
            cy={position.y}
            r={shape.radius || 30}
            fill={color}
            stroke={isSelected ? "#007bff" : "#000"}
            strokeWidth={isSelected ? "3" : "2"}
            style={{ 
              cursor: isDragging && draggedLocation?._id === location._id ? 'grabbing' : 
                      selectedTool === 'select' ? 'grab' : 'pointer'
            }}
            onClick={(e) => {
              e.stopPropagation();
              console.log('Location clicked:', { location, selectedTool, getSelectedCount: getSelectedCount() });
              
              // Handle multi-selection with Ctrl key
              if (e.ctrlKey || e.metaKey) {
                if (isElementSelected('locations', location._id)) {
                  removeFromSelection('locations', location._id);
                } else {
                  addToSelection('locations', location._id);
                }
              } else {
                // Single selection - clear other selections and select this location
                clearSelection();
                addToSelection('locations', location._id);
              }
              
              console.log('After location selection update:', { selectedElements, getSelectedCount: getSelectedCount() });
              
              setSelectedLocationId(location._id);
              setSelectedLocation(location);
            }}
            onDoubleClick={() => {
              setViewingLocation(location);
              setIsLocationDetailsOpen(true);
            }}
            onContextMenu={(e) => handleContextMenu(e, location)}
          />
          <text
            x={getTextPosition(position, shape, textPosition).x}
            y={getTextPosition(position, shape, textPosition).y}
            textAnchor={getTextPosition(position, shape, textPosition).textAnchor}
            dominantBaseline={getTextPosition(position, shape, textPosition).dominantBaseline}
            fontSize="12"
            fill="#000"
            pointerEvents="none"
          >
            {name}
          </text>
          
          {/* Arrow resize handles - only show when selected */}
          {isSelected && (
            <>
              {/* Horizontal arrow */}
              <g>
                <line
                  x1={position.x + (shape.radius || 30) + 10}
                  y1={position.y}
                  x2={position.x + (shape.radius || 30) + 30}
                  y2={position.y}
                  stroke="#007bff"
                  strokeWidth="2"
                  markerEnd="url(#arrowhead)"
                />
                <circle
                  cx={position.x + (shape.radius || 30) + 20}
                  cy={position.y}
                  r="8"
                  fill="#007bff"
                  stroke="#fff"
                  strokeWidth="2"
                  style={{ cursor: 'ew-resize' }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    handleArrowResizeStart(location._id, 'horizontal');
                  }}
                />
              </g>
              
              {/* Vertical arrow */}
              <g>
                <line
                  x1={position.x}
                  y1={position.y + (shape.radius || 30) + 10}
                  x2={position.x}
                  y2={position.y + (shape.radius || 30) + 30}
                  stroke="#007bff"
                  strokeWidth="2"
                  markerEnd="url(#arrowhead-vertical)"
                />
                <circle
                  cx={position.x}
                  cy={position.y + (shape.radius || 30) + 20}
                  r="8"
                  fill="#007bff"
                  stroke="#fff"
                  strokeWidth="2"
                  style={{ cursor: 'ns-resize' }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    handleArrowResizeStart(location._id, 'vertical');
                  }}
                />
              </g>
            </>
          )}
        </g>
      );
    } else {
      return (
        <g key={location._id}>
          <rect
            x={position.x}
            y={position.y}
            width={shape.width}
            height={shape.height}
            fill={color}
            stroke={isSelected ? "#007bff" : "#000"}
            strokeWidth={isSelected ? "3" : "2"}
            style={{ 
              cursor: isDragging && draggedLocation?._id === location._id ? 'grabbing' : 
                      selectedTool === 'select' ? 'grab' : 'pointer'
            }}
            onClick={(e) => {
              e.stopPropagation();
              console.log('Location clicked:', { location, selectedTool, getSelectedCount: getSelectedCount() });
              
              // Handle multi-selection with Ctrl key
              if (e.ctrlKey || e.metaKey) {
                if (isElementSelected('locations', location._id)) {
                  removeFromSelection('locations', location._id);
                } else {
                  addToSelection('locations', location._id);
                }
              } else {
                // Single selection - clear other selections and select this location
                clearSelection();
                addToSelection('locations', location._id);
              }
              
              console.log('After location selection update:', { selectedElements, getSelectedCount: getSelectedCount() });
              
              setSelectedLocationId(location._id);
              setSelectedLocation(location);
            }}
            onDoubleClick={() => {
              setViewingLocation(location);
              setIsLocationDetailsOpen(true);
            }}
            onContextMenu={(e) => handleContextMenu(e, location)}
          />
          <text
            x={getTextPosition(position, shape, textPosition).x}
            y={getTextPosition(position, shape, textPosition).y}
            textAnchor={getTextPosition(position, shape, textPosition).textAnchor}
            dominantBaseline={getTextPosition(position, shape, textPosition).dominantBaseline}
            fontSize="12"
            fill="#000"
            pointerEvents="none"
          >
            {name}
          </text>
          
          {/* Arrow resize handles - only show when selected */}
          {isSelected && (
            <>
              {/* Horizontal arrow */}
              <g>
                <line
                  x1={position.x + shape.width + 10}
                  y1={position.y + shape.height / 2}
                  x2={position.x + shape.width + 30}
                  y2={position.y + shape.height / 2}
                  stroke="#007bff"
                  strokeWidth="2"
                  markerEnd="url(#arrowhead)"
                />
                <circle
                  cx={position.x + shape.width + 20}
                  cy={position.y + shape.height / 2}
                  r="8"
                  fill="#007bff"
                  stroke="#fff"
                  strokeWidth="2"
                  style={{ cursor: 'ew-resize' }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    handleArrowResizeStart(location._id, 'horizontal');
                  }}
                />
              </g>
              
              {/* Vertical arrow */}
              <g>
                <line
                  x1={position.x + shape.width / 2}
                  y1={position.y + shape.height + 10}
                  x2={position.x + shape.width / 2}
                  y2={position.y + shape.height + 30}
                  stroke="#007bff"
                  strokeWidth="2"
                  markerEnd="url(#arrowhead-vertical)"
                />
                <circle
                  cx={position.x + shape.width / 2}
                  cy={position.y + shape.height + 20}
                  r="8"
                  fill="#007bff"
                  stroke="#fff"
                  strokeWidth="2"
                  style={{ cursor: 'ns-resize' }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    handleArrowResizeStart(location._id, 'vertical');
                  }}
                />
              </g>
            </>
          )}
        </g>
      );
    }
  };

  // Render text element
  const renderText = (text) => {
    const isSelected = selectedText?._id === text._id || isElementSelected('texts', text._id);
    const handleSize = 24;
    const handleOffset = text.style.fontSize * 1.2;
    
    return (
      <g key={text._id}>
        <g transform={`rotate(${text.rotation || 0}, ${text.position.x}, ${text.position.y})`}>
          <text
            x={text.position.x}
            y={text.position.y}
            fontSize={text.style.fontSize}
            fontWeight={text.style.fontWeight}
            fontStyle={text.style.fontStyle}
            fill={text.style.color}
            textAnchor="middle"
            dominantBaseline="middle"
            style={{ cursor: draggedText?._id === text._id ? 'grabbing' : 'grab' }}
            onClick={(e) => {
              e.stopPropagation();
              console.log('Clicking text:', text);
              console.log('Text clicked:', { text, selectedTool, getSelectedCount: getSelectedCount() });
              
              // Handle multi-selection with Ctrl key
              if (e.ctrlKey || e.metaKey) {
                if (isElementSelected('texts', text._id)) {
                  removeFromSelection('texts', text._id);
                } else {
                  addToSelection('texts', text._id);
                }
              } else {
                // Single selection - clear other selections and select this text
                clearSelection();
                addToSelection('texts', text._id);
              }
              
              console.log('After text selection update:', { selectedElements, getSelectedCount: getSelectedCount() });
              
              setSelectedText(text);
            }}
            onDoubleClick={(e) => {
              e.stopPropagation();
              console.log('Double-clicking text:', text);
              console.log('Setting editingText to:', text);
              setEditingText(text);
              setIsTextDialogOpen(true);
            }}
                         onMouseDown={(e) => {
              e.stopPropagation();
              console.log('Text mouse down triggered:', { text, selectedTool, isDragging, isDraggingLine, getSelectedCount: getSelectedCount() });
              // Only set drag state if we're in select mode and not already dragging
              // Don't set drag state on single clicks - only on actual drag operations
              if (selectedTool === 'select' && !isDragging && !isDraggingLine) {
                // Store the initial mouse position to detect actual dragging
                const rect = svgRef.current.getBoundingClientRect();
                const scale = rect.width / canvasSize.width;
                const mouseX = (e.clientX - rect.left) / scale;
                const mouseY = (e.clientY - rect.top) / scale;
                
                console.log('Text mouse down - setting up drag detection:', { mouseX, mouseY, textPosition: text.position });
                
                // Only set drag state if we're actually moving the mouse (not just clicking)
                const handleMouseMove = (moveEvent) => {
                  const moveRect = svgRef.current.getBoundingClientRect();
                  const moveScale = moveRect.width / canvasSize.width;
                  const moveX = (moveEvent.clientX - moveRect.left) / moveScale;
                  const moveY = (moveEvent.clientY - moveRect.top) / moveScale;
                  
                  // If mouse has moved more than 5 pixels, start dragging
                  if (Math.abs(moveX - mouseX) > 5 || Math.abs(moveY - mouseY) > 5) {
                    console.log('Text drag detected, starting drag:', { moveX, moveY, mouseX, mouseY });
                    setDraggedText(text);
                    setTextDragOffset({
                      x: mouseX - text.position.x,
                      y: mouseY - text.position.y
                    });
                    
                    // If we have multiple elements selected, start multi-selection dragging
                    if (getSelectedCount() > 1) {
                      console.log(`Starting multi-selection text drag with ${getSelectedCount()} elements selected`);
                      console.log('Selected elements:', selectedElements);
                      setIsMultiSelecting(true);
                      setMultiSelectOffset({
                        x: mouseX - text.position.x,
                        y: mouseY - text.position.y
                      });
                    } else {
                      console.log('Single text selection, not starting multi-selection');
                    }
                    
                    // Remove the event listener once dragging starts
                    document.removeEventListener('mousemove', handleMouseMove);
                  }
                };
                
                // Add temporary mouse move listener to detect actual dragging
                document.addEventListener('mousemove', handleMouseMove);
                
                // Remove listener after a short delay if no dragging occurred
                setTimeout(() => {
                  document.removeEventListener('mousemove', handleMouseMove);
                }, 100);
              } else {
                console.log('Text mouse down - not setting up drag detection:', { selectedTool, isDragging, isDraggingLine });
              }
            }}
          >
            {text.content}
          </text>
          {isSelected && (
            <rect
              x={text.position.x - text.content.length * text.style.fontSize * 0.3}
              y={text.position.y - text.style.fontSize * 0.7}
              width={text.content.length * text.style.fontSize * 0.6}
              height={text.style.fontSize * 1.4}
              fill="none"
              stroke="#007bff"
              strokeWidth="2"
              strokeDasharray="5,5"
            />
          )}
        </g>
        {/* Rotation handle - rendered outside the rotated group */}
        {isSelected && (
          <g>
            <circle
              cx={text.position.x}
              cy={text.position.y - handleOffset}
              r={handleSize / 2}
              fill="#007bff"
              fillOpacity="0.1"
              stroke="#007bff"
              strokeWidth="2"
              style={{ cursor: isRotating ? 'grabbing' : 'grab' }}
              onMouseDown={(e) => handleRotationStart(e, text)}
            />
            <foreignObject
              x={text.position.x - 12}
              y={text.position.y - handleOffset - 12}
              width="24"
              height="24"
              style={{ pointerEvents: 'none' }}
            >
              <RotateIcon
                style={{
                  width: '24px',
                  height: '24px',
                  color: '#007bff'
                }}
              />
            </foreignObject>
          </g>
        )}
      </g>
    );
  };

  // Render line as SVG element with click handler and drag support
  const renderLine = (line, index) => {
    if (!line.points || line.points.length < 2) return null;
    
    const points = line.points.map(p => `${p.x},${p.y}`).join(' ');
    
    return (
      <g key={index}>
        {/* Invisible wider hit area for easier selection */}
        <polyline
          points={points}
          fill="none"
          stroke="transparent"
          strokeWidth="20"
          style={{ cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            console.log('Line clicked:', { line, index, selectedTool, getSelectedCount: getSelectedCount() });
            
            // Handle multi-selection with Ctrl key
            if (e.ctrlKey || e.metaKey) {
              if (isElementSelected('lines', line._id)) {
                removeFromSelection('lines', line._id);
              } else {
                addToSelection('lines', line._id);
              }
            } else {
              // Single selection - clear other selections and select this line
              clearSelection();
              addToSelection('lines', line._id);
            }
            
            console.log('After line selection update:', { selectedElements, getSelectedCount: getSelectedCount() });
            
            setSelectedLine(index);
          }}
          onMouseDown={(e) => handleLineMouseDown(e, line, index)}
          onContextMenu={(e) => handleLineContextMenu(e, index)}
        />
        {/* Visible line */}
        <polyline
          points={points}
          fill="none"
          stroke={line.color || '#000'}
          strokeWidth={line.strokeWidth || 2}
          style={{ pointerEvents: 'none' }}
        />
        {(selectedLine === index || isElementSelected('lines', line._id)) && (
          <rect
            x={Math.min(...line.points.map(p => p.x)) - 5}
            y={Math.min(...line.points.map(p => p.y)) - 5}
            width={Math.max(...line.points.map(p => p.x)) - Math.min(...line.points.map(p => p.x)) + 10}
            height={Math.max(...line.points.map(p => p.y)) - Math.min(...line.points.map(p => p.y)) + 10}
            fill="none"
            stroke="#ff0000"
            strokeWidth="2"
            strokeDasharray="5,5"
          />
        )}
      </g>
    );
  };

  return (
    <Box sx={{ p: 2, height: '100vh', overflow: 'hidden' }}>
      <Typography variant="h4" gutterBottom>
        Factory Maps
      </Typography>
      
      <Grid container spacing={2} sx={{ height: 'calc(100vh - 100px)' }}>
        {/* Maps List */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, height: '100%', overflow: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Maps</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setIsCreateDialogOpen(true)}
                size="small"
              >
                New Map
              </Button>
            </Box>
            
            <List>
              {maps.map((map) => (
                <ListItem
                  key={map._id}
                  selected={selectedMap?._id === map._id}
                  onClick={() => setSelectedMap(map)}
                  sx={{ cursor: 'pointer' }}
                  secondaryAction={
                    <Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedMap(map);
                          setIsEditDialogOpen(true);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteMap(map._id);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemIcon>
                    <MapIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={map.title}
                    secondary={`${map.factory} • ${map.locations?.length || 0} locations`}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
        
        {/* Canvas Area */}
        <Grid item xs={12} md={9}>
          <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            {selectedMap ? (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">{selectedMap.title}</Typography>
                  <Box>
                    <Button
                      variant="outlined"
                      startIcon={<SearchIcon />}
                      onClick={() => setIsSearchDialogOpen(true)}
                      sx={{ mr: 1 }}
                    >
                      Search
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<LocationIcon />}
                      onClick={() => {
                        setSelectedTool('location');
                        setEditingLocation(null);
                      }}
                      sx={{ mr: 1 }}
                    >
                      Add Location
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CategoryIcon />}
                      onClick={() => {
                        setSelectedTool('line');
                        setIsDrawingLine(false);
                        setLineStart(null);
                      }}
                      sx={{ mr: 1 }}
                    >
                      Draw Line
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<TextIcon />}
                      onClick={() => {
                        setSelectedTool('text');
                        setIsDrawingLine(false);
                        setLineStart(null);
                      }}
                      sx={{ mr: 1 }}
                    >
                      Add Text
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setSelectedTool('select');
                        setSelectedLine(null);
                      }}
                      sx={{ mr: 1 }}
                    >
                      Select
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleUndo}
                      disabled={actionHistory.length === 0}
                      sx={{ mr: 1 }}
                    >
                      Undo
                    </Button>
                                         <Button
                       variant="outlined"
                       onClick={() => setShowGrid(!showGrid)}
                       sx={{ mr: 1 }}
                     >
                       {showGrid ? 'Hide Grid' : 'Show Grid'}
                     </Button>
                     {getSelectedCount() > 0 && (
                       <Button
                         variant="outlined"
                         onClick={clearSelection}
                         sx={{ mr: 1 }}
                       >
                         Clear Selection ({getSelectedCount()})
                       </Button>
                     )}
                     <Button
                       variant="outlined"
                       color="secondary"
                       onClick={() => {
                         console.log('Test button clicked');
                         console.log('Current selectedElements:', selectedElements);
                         console.log('Current getSelectedCount():', getSelectedCount());
                         console.log('Current isMultiSelecting:', isMultiSelecting);
                         // Test multi-selection by selecting first few elements
                         if (selectedMap && selectedMap.locations && selectedMap.locations.length > 0) {
                           const testSelection = {
                             locations: selectedMap.locations.slice(0, 2).map(loc => loc._id),
                             texts: selectedMap.texts ? selectedMap.texts.slice(0, 1).map(text => text._id) : [],
                             lines: selectedMap.lines ? selectedMap.lines.slice(0, 1).map(line => line._id) : []
                           };
                           console.log('Setting test selection:', testSelection);
                           setSelectedElements(testSelection);
                         }
                       }}
                       sx={{ mr: 1 }}
                     >
                       Test Multi-Select
                     </Button>
                    {selectedLine !== null && (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => handleDeleteLine(selectedLine)}
                      >
                        Delete Line
                      </Button>
                    )}
                  </Box>
                </Box>
                
                <Box sx={{ flexGrow: 1, border: '1px solid #ccc', borderRadius: 1, overflow: 'hidden' }}>
                  <svg
                    ref={svgRef}
                    width={canvasSize.width}
                    height={canvasSize.height}
                    onClick={handleSvgClick}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onContextMenu={(e) => handleContextMenu(e, null)} // Handle global context menu
                    style={{ 
                      cursor: selectedTool === 'location' ? 'crosshair' : 
                              selectedTool === 'line' ? 'crosshair' : 'default',
                      backgroundColor: selectedMap.canvas?.background || '#ffffff',
                      maxWidth: '100%',
                      maxHeight: '100%',
                      border: '2px solid #1976d2',
                      borderRadius: '4px'
                    }}
                  >
                    {/* SVG marker definitions for arrows */}
                    <defs>
                      <marker
                        id="arrowhead"
                        markerWidth="10"
                        markerHeight="7"
                        refX="9"
                        refY="3.5"
                        orient="auto"
                      >
                        <polygon points="0 0, 10 3.5, 0 7" fill="#007bff" />
                      </marker>
                      <marker
                        id="arrowhead-vertical"
                        markerWidth="7"
                        markerHeight="10"
                        refX="3.5"
                        refY="9"
                        orient="auto"
                      >
                        <polygon points="0 0, 3.5 10, 7 0" fill="#007bff" />
                      </marker>
                    </defs>
                    {/* Background */}
                    <rect
                      x="0"
                      y="0"
                      width={canvasSize.width}
                      height={canvasSize.height}
                      fill={selectedMap.canvas?.background || '#ffffff'}
                    />
                    
                    {/* Canvas boundary indicator */}
                    <rect
                      x="0"
                      y="0"
                      width={canvasSize.width}
                      height={canvasSize.height}
                      fill="none"
                      stroke="#1976d2"
                      strokeWidth="3"
                      strokeDasharray="10,5"
                      opacity="0.6"
                    />
                    
                    {/* Canvas size labels */}
                    <text
                      x="10"
                      y="20"
                      fontSize="12"
                      fill="#1976d2"
                      fontWeight="bold"
                    >
                      Canvas: {canvasSize.width} × {canvasSize.height}
                    </text>
                    
                    {/* Lines */}
                    {selectedMap.lines?.map((line, index) => renderLine(line, index))}
                    
                    {/* Current drawing line */}
                    {currentLine.length === 2 && (
                      <polyline
                        points={currentLine.map(p => `${p.x},${p.y}`).join(' ')}
                        fill="none"
                        stroke="#000"
                        strokeWidth="2"
                        strokeDasharray="5,5"
                      />
                    )}
                    
                    {/* Locations */}
                    {selectedMap.locations?.map(renderLocation)}

                    {/* Text Elements */}
                    {selectedMap.texts?.map(renderText)}

                    {/* Grid */}
                    {renderGrid()}
                  </svg>
                </Box>
                
                {/* Instructions */}
                {selectedTool === 'line' && (
                  <Box sx={{ mt: 1, p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
                    <Typography variant="body2" color="info.contrastText">
                      Click to start line, click again to finish line
                    </Typography>
                  </Box>
                )}
                {selectedTool === 'select' && (
                  <Box sx={{ mt: 1, p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                    <Typography variant="body2" color="success.contrastText">
                      Click and drag locations to move them
                    </Typography>
                    {getSelectedCount() > 1 && (
                      <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                        {getSelectedCount()} elements selected - Hold Ctrl/Cmd to multi-select
                      </Typography>
                    )}
                    {getSelectedCount() > 0 && (
                      <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontSize: '0.8rem' }}>
                        Debug - Selected: L:{selectedElements.locations.length} T:{selectedElements.texts.length} L:{selectedElements.lines.length}
                      </Typography>
                    )}
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography variant="h6" color="textSecondary">
                  Select a map to start editing
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
      
      {/* Create Map Dialog */}
      <Dialog open={isCreateDialogOpen} onClose={() => setIsCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Map</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Map Title"
            value={newMap.title}
            onChange={(e) => setNewMap({ ...newMap, title: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Factory Name"
            value={newMap.factory}
            onChange={(e) => setNewMap({ ...newMap, factory: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Description"
            value={newMap.description}
            onChange={(e) => setNewMap({ ...newMap, description: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateMap} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>
      
      {/* Edit Map Dialog */}
      <Dialog open={isEditDialogOpen} onClose={() => setIsEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Map</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Map Title"
            value={selectedMap?.title || ''}
            onChange={(e) => setSelectedMap({ ...selectedMap, title: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Factory Name"
            value={selectedMap?.factory || ''}
            onChange={(e) => setSelectedMap({ ...selectedMap, factory: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Description"
            value={selectedMap?.description || ''}
            onChange={(e) => setSelectedMap({ ...selectedMap, description: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateMap} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
      
      {/* Location Dialog */}
      <Dialog open={isLocationDialogOpen} onClose={() => setIsLocationDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingLocation?._id ? 'Edit Location' : 'Add Location'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Location Name"
            value={editingLocation?.name || ''}
            onChange={(e) => setEditingLocation({ ...editingLocation, name: e.target.value })}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Shape Type</InputLabel>
            <Select
              value={editingLocation?.shape?.type || 'rectangle'}
              onChange={(e) => setEditingLocation({ 
                ...editingLocation, 
                shape: { ...editingLocation.shape, type: e.target.value }
              })}
            >
              <MenuItem value="rectangle">Rectangle</MenuItem>
              <MenuItem value="circle">Circle</MenuItem>
            </Select>
          </FormControl>
          
          {/* Color Picker */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Color
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {['#ff5722', '#2196f3', '#4caf50', '#ff9800', '#9c27b0', '#f44336', '#795548', '#607d8b'].map((color) => (
                <Box
                  key={color}
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: color,
                    border: editingLocation?.color === color ? '3px solid #000' : '2px solid #ccc',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      transition: 'transform 0.2s'
                    }
                  }}
                  onClick={() => setEditingLocation({ ...editingLocation, color })}
                />
              ))}
            </Box>
          </Box>
          
          <TextField
            fullWidth
            label="Description"
            value={editingLocation?.description || ''}
            onChange={(e) => setEditingLocation({ ...editingLocation, description: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
          <TextField
            fullWidth
            label="Tags (comma separated)"
            value={editingLocation?.tags?.join(', ') || ''}
            onChange={(e) => setEditingLocation({ 
              ...editingLocation, 
              tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)
            })}
            margin="normal"
          />
          
          {/* Text Position Selector */}
          <FormControl fullWidth margin="normal">
            <InputLabel>Text Position</InputLabel>
            <Select
              value={editingLocation?.textPosition || 'bottom'}
              onChange={(e) => {
                console.log('Text position changed to:', e.target.value);
                console.log('Current editingLocation:', editingLocation);
                setEditingLocation({ 
                  ...editingLocation, 
                  textPosition: e.target.value 
                });
              }}
            >
              <MenuItem value="bottom">Bottom</MenuItem>
              <MenuItem value="top">Top</MenuItem>
              <MenuItem value="left">Left</MenuItem>
              <MenuItem value="right">Right</MenuItem>
            </Select>
            <FormHelperText>
              Choose where the location name appears relative to the shape
            </FormHelperText>
          </FormControl>
          
          {/* Photo Upload */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Location Photo
            </Typography>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="location-photo-upload"
              type="file"
              onChange={handlePhotoUpload}
            />
            <label htmlFor="location-photo-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<PhotoIcon />}
                fullWidth
              >
                Upload Photo
              </Button>
            </label>
            {editingLocation?.photo && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <img
                  src={editingLocation.photo}
                  alt="Location"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '200px',
                    borderRadius: '8px',
                    border: '1px solid #ccc'
                  }}
                />
                <Button
                  size="small"
                  color="error"
                  onClick={() => setEditingLocation({ ...editingLocation, photo: null })}
                  sx={{ mt: 1 }}
                >
                  Remove Photo
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsLocationDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              console.log('Saving location with data:', editingLocation);
              console.log('textPosition in editingLocation:', editingLocation?.textPosition);
              if (editingLocation?._id) {
                handleUpdateLocation(editingLocation._id, editingLocation);
              } else {
                handleAddLocation(editingLocation);
              }
            }} 
            variant="contained"
          >
            {editingLocation?._id ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Search Dialog */}
      <Dialog open={isSearchDialogOpen} onClose={() => setIsSearchDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Search Locations</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Search query"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            margin="normal"
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            sx={{ mt: 2 }}
          >
            Search
          </Button>
          
          {searchResults.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Search Results
              </Typography>
              {searchResults.map((result, index) => (
                <Card key={index} sx={{ mb: 1 }}>
                  <CardContent>
                    <Typography variant="h6">{result.location.name}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {result.mapTitle} • {result.factory}
                    </Typography>
                    {result.location.description && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {result.location.description}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsSearchDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* Location Details Dialog */}
      <Dialog open={isLocationDetailsOpen} onClose={() => setIsLocationDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Location Details: {viewingLocation?.name}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Information
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Name:</strong> {viewingLocation?.name}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Type:</strong> {viewingLocation?.shape?.type}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Position:</strong> ({viewingLocation?.position?.x}, {viewingLocation?.position?.y})
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Size:</strong> {viewingLocation?.shape?.type === 'circle' 
                  ? `Radius: ${viewingLocation?.shape?.radius}px`
                  : `${viewingLocation?.shape?.width} × ${viewingLocation?.shape?.height}px`
                }
              </Typography>
              {viewingLocation?.description && (
                <Typography variant="body1" gutterBottom>
                  <strong>Description:</strong> {viewingLocation.description}
                </Typography>
              )}
              {viewingLocation?.tags && viewingLocation.tags.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body1" gutterBottom>
                    <strong>Tags:</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {viewingLocation.tags.map((tag, index) => (
                      <Chip key={index} label={tag} size="small" />
                    ))}
                  </Box>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Photo
              </Typography>
              {viewingLocation?.photo ? (
                <img
                  src={viewingLocation.photo}
                  alt={viewingLocation.name}
                  style={{
                    width: '100%',
                    maxHeight: '300px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    border: '1px solid #ccc'
                  }}
                />
              ) : (
                <Box sx={{ 
                  height: 200, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  border: '2px dashed #ccc',
                  borderRadius: '8px',
                  backgroundColor: '#f5f5f5'
                }}>
                  <Typography variant="body2" color="textSecondary">
                    No photo uploaded
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              console.log('Setting editingLocation from details dialog:', viewingLocation);
              console.log('textPosition in viewingLocation:', viewingLocation.textPosition);
              // Ensure textPosition has a default value if undefined
              const locationWithTextPosition = {
                ...viewingLocation,
                textPosition: viewingLocation.textPosition || 'bottom'
              };
              setEditingLocation(locationWithTextPosition);
              setIsLocationDetailsOpen(false);
              setIsLocationDialogOpen(true);
            }}
          >
            Edit Location
          </Button>
          <Button onClick={() => setIsLocationDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* Floating Action Button for Mobile */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={() => setIsCreateDialogOpen(true)}
        >
          <AddIcon />
        </Fab>
      )}
       
      {/* Context Menu */}
      <Menu
        open={contextMenu.open}
        onClose={() => setContextMenu({ open: false, x: 0, y: 0, location: null })}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu.open
            ? { top: contextMenu.y, left: contextMenu.x }
            : undefined
        }
      >
        {contextMenu.location && (
          <>
            <MenuItem onClick={() => handleManualResize(contextMenu.location)}>
              <EditIcon sx={{ mr: 1 }} />
              Resize Manually
            </MenuItem>
            <MenuItem onClick={() => {
              console.log('Setting editingLocation from context menu:', contextMenu.location);
              console.log('textPosition in context menu location:', contextMenu.location.textPosition);
              // Ensure textPosition has a default value if undefined
              const locationWithTextPosition = {
                ...contextMenu.location,
                textPosition: contextMenu.location.textPosition || 'bottom'
              };
              setEditingLocation(locationWithTextPosition);
              setIsLocationDialogOpen(true);
              setContextMenu({ open: false, x: 0, y: 0, location: null });
            }}>
              <EditIcon sx={{ mr: 1 }} />
              Edit Location
            </MenuItem>
            <MenuItem onClick={() => {
              handleDeleteLocation(contextMenu.location._id);
              setContextMenu({ open: false, x: 0, y: 0, location: null });
            }}>
              <DeleteIcon sx={{ mr: 1 }} />
              Delete Location
            </MenuItem>
          </>
        )}
        {!contextMenu.location && (
          <MenuItem onClick={() => {
            setSelectedTool('location');
            setContextMenu({ open: false, x: 0, y: 0, location: null });
          }}>
            <AddIcon sx={{ mr: 1 }} />
            Add Location Here
          </MenuItem>
        )}
      </Menu>

      {/* Line Context Menu */}
      <Menu
        open={lineContextMenu.open}
        onClose={() => setLineContextMenu({ open: false, x: 0, y: 0, lineIndex: null })}
        anchorReference="anchorPosition"
        anchorPosition={
          lineContextMenu.open
            ? { top: lineContextMenu.y, left: lineContextMenu.x }
            : undefined
        }
      >
        <MenuItem onClick={() => handleLineColorChange(lineContextMenu.lineIndex, '#ff0000')}>
          <Box sx={{ width: 20, height: 20, bgcolor: '#ff0000', mr: 1, borderRadius: '50%' }} />
          Red
        </MenuItem>
        <MenuItem onClick={() => handleLineColorChange(lineContextMenu.lineIndex, '#00ff00')}>
          <Box sx={{ width: 20, height: 20, bgcolor: '#00ff00', mr: 1, borderRadius: '50%' }} />
          Green
        </MenuItem>
        <MenuItem onClick={() => handleLineColorChange(lineContextMenu.lineIndex, '#0000ff')}>
          <Box sx={{ width: 20, height: 20, bgcolor: '#0000ff', mr: 1, borderRadius: '50%' }} />
          Blue
        </MenuItem>
        <MenuItem onClick={() => handleLineColorChange(lineContextMenu.lineIndex, '#ffff00')}>
          <Box sx={{ width: 20, height: 20, bgcolor: '#ffff00', mr: 1, borderRadius: '50%' }} />
          Yellow
        </MenuItem>
        <MenuItem onClick={() => handleLineColorChange(lineContextMenu.lineIndex, '#ff00ff')}>
          <Box sx={{ width: 20, height: 20, bgcolor: '#ff00ff', mr: 1, borderRadius: '50%' }} />
          Magenta
        </MenuItem>
        <MenuItem onClick={() => handleLineColorChange(lineContextMenu.lineIndex, '#00ffff')}>
          <Box sx={{ width: 20, height: 20, bgcolor: '#00ffff', mr: 1, borderRadius: '50%' }} />
          Cyan
        </MenuItem>
        <MenuItem onClick={() => handleLineColorChange(lineContextMenu.lineIndex, '#000000')}>
          <Box sx={{ width: 20, height: 20, bgcolor: '#000000', mr: 1, borderRadius: '50%' }} />
          Black
        </MenuItem>
        <MenuItem onClick={() => handleLineColorChange(lineContextMenu.lineIndex, '#ffffff')}>
          <Box sx={{ width: 20, height: 20, bgcolor: '#ffffff', mr: 1, borderRadius: '50%', border: '1px solid #ccc' }} />
          White
        </MenuItem>
      </Menu>
       
      {/* Text Dialog */}
             <Dialog open={isTextDialogOpen} onClose={() => {
         console.log('Text dialog closing');
         setIsTextDialogOpen(false);
       }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingText?._id ? 'Edit Text' : 'Add Text'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Text Content"
            value={editingText?.content || ''}
            onChange={(e) => setEditingText({ ...editingText, content: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
          
          {/* Font Size */}
          <FormControl fullWidth margin="normal">
            <InputLabel>Font Size</InputLabel>
            <Select
              value={editingText?.style?.fontSize || 16}
              onChange={(e) => setEditingText({
                ...editingText,
                style: { ...editingText?.style, fontSize: e.target.value }
              })}
            >
              {[12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48].map(size => (
                <MenuItem key={size} value={size}>{size}px</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Rotation Control */}
          <FormControl fullWidth margin="normal">
            <Typography variant="subtitle2" gutterBottom>
              Rotation (degrees)
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Slider
                value={editingText?.rotation || 0}
                onChange={(e, value) => setEditingText({
                  ...editingText,
                  rotation: value
                })}
                min={-180}
                max={180}
                marks={[
                  { value: -180, label: '-180°' },
                  { value: -90, label: '-90°' },
                  { value: 0, label: '0°' },
                  { value: 90, label: '90°' },
                  { value: 180, label: '180°' }
                ]}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${value}°`}
              />
            </Box>
          </FormControl>
          
          {/* Text Style Controls */}
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button
              variant={editingText?.style?.fontWeight === 'bold' ? 'contained' : 'outlined'}
              startIcon={<BoldIcon />}
              onClick={() => setEditingText({
                ...editingText,
                style: {
                  ...editingText?.style,
                  fontWeight: editingText?.style?.fontWeight === 'bold' ? 'normal' : 'bold'
                }
              })}
            >
              Bold
            </Button>
            <Button
              variant={editingText?.style?.fontStyle === 'italic' ? 'contained' : 'outlined'}
              startIcon={<ItalicIcon />}
              onClick={() => setEditingText({
                ...editingText,
                style: {
                  ...editingText?.style,
                  fontStyle: editingText?.style?.fontStyle === 'italic' ? 'normal' : 'italic'
                }
              })}
            >
              Italic
            </Button>
          </Box>
          
          {/* Color Picker */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Text Color
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {['#000000', '#ff0000', '#00ff00', '#0000ff', '#ff00ff', '#00ffff', '#ffff00', '#ffffff'].map((color) => (
                <Box
                  key={color}
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: color,
                    border: editingText?.style?.color === color ? '3px solid #000' : '2px solid #ccc',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      transition: 'transform 0.2s'
                    }
                  }}
                  onClick={() => setEditingText({
                    ...editingText,
                    style: { ...editingText?.style, color }
                  })}
                />
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsTextDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              if (editingText?._id) {
                handleUpdateText(editingText._id, editingText);
              } else {
                handleAddText(editingText);
              }
            }}
            variant="contained"
          >
            {editingText?._id ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual Resize Dialog */}
      <Dialog open={isResizeDialogOpen} onClose={() => setIsResizeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Resize Location: {resizingLocation?.name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Enter the new dimensions for this location:
          </Typography>
          
          {resizingLocation?.shape?.type === 'circle' ? (
            <TextField
              fullWidth
              label="Radius (pixels)"
              type="number"
              value={resizingLocation?.shape?.radius || 30}
              onChange={(e) => setResizingLocation({
                ...resizingLocation,
                shape: { ...resizingLocation.shape, radius: e.target.value }
              })}
              margin="normal"
              inputProps={{ min: 10, max: 200 }}
            />
          ) : (
            <>
              <TextField
                fullWidth
                label="Width (pixels)"
                type="number"
                value={resizingLocation?.shape?.width || 100}
                onChange={(e) => setResizingLocation({
                  ...resizingLocation,
                  shape: { ...resizingLocation.shape, width: e.target.value }
                })}
                margin="normal"
                inputProps={{ min: 20, max: 500 }}
              />
              <TextField
                fullWidth
                label="Height (pixels)"
                type="number"
                value={resizingLocation?.shape?.height || 60}
                onChange={(e) => setResizingLocation({
                  ...resizingLocation,
                  shape: { ...resizingLocation.shape, height: e.target.value }
                })}
                margin="normal"
                inputProps={{ min: 20, max: 500 }}
              />
            </>
          )}
          
          <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
            Tip: Use the grid toggle to align locations perfectly!
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsResizeDialogOpen(false)}>Cancel</Button>
          <Button onClick={applyManualResize} variant="contained">Apply</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default FactoryMapsApp; 